import { Col, Row } from "react-grid-system";
import { theme } from "../../../theme";
import { Document, Page, pdfjs } from "react-pdf";
import { useEffect, useMemo, useState } from "react";
import Subtitle from "../../atoms/typography/Subtitle";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Body from "../../atoms/typography/Body";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import { FileIconOne } from "../../atoms/icons/FileIcons";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import {
  ChevronLeftIconLarge,
  ChevronRightIconLarge,
} from "../../atoms/icons/ChevronIcons";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { DownloadIcon } from "../../atoms/icons/DownloadIcon";
import { useFile } from "../../../graphql/hooks/useFile";
import Alert from "../Alert";
import { useTranslation } from "react-i18next";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import Subheading from "../../atoms/typography/Subheading";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface DocumentViewerFiles {
  fileName: string;
  fileId: string;
  documentId: string;
}

interface Props {
  documentId?: string;
  files: Array<DocumentViewerFiles>;
}

const PreviewDocumentsModalContent: React.FC<Props> = ({ files }) => {
  const [selectedFile, setSelectedFile] = useState<{
    fileName: string;
    fileId: string;
  }>(files[0] ?? "");
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const onClickFile = (file: { fileName: string; fileId: string }) => {
    setSelectedFile(file);
  };

  const { file, error, loading } = useFile(selectedFile.fileId);

  function base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file?.content && file?.contentType === "application/pdf") {
      try {
        // Some APIs include "data:application/pdf;base64,..." prefix → strip it
        const base64 = file.content.split(",").pop() || file.content;
        const byteArray = base64ToUint8Array(base64);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        setPdfUrl(url);

        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (error) {
        console.error("Failed to decode Base64 PDF content:", error);
        setPdfUrl(null);
      }
    } else {
      setPdfUrl(null);
    }
  }, [file]);

  const memoizedFile = useMemo(() => pdfUrl, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const handleDownload = () => {
    if (!file?.content || !file.contentType) {
      console.error("Missing file content or contentType.");
      return;
    }

    try {
      // Clean and decode Base64
      const base64 = file.content.split(",").pop() || "";
      const binaryString = atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create a Blob
      const blob = new Blob([bytes], { type: file.contentType });

      // Create Blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.fileName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  return (
    <div style={{ minHeight: "80vh" }}>
      <Row justify="center">
        <Subtitle>{selectedFile.fileName}</Subtitle>
      </Row>

      <HorizontalSpacer spacing={1} />
      <Row>
        <Col lg={3}>
          <Subheading>
            {files.length} {t("documentHandler.document").toLowerCase()}
          </Subheading>
          <HorizontalSpacer spacing={2} />
          {files.map((file) => (
            <div key={file.fileId}>
              <TextLinkButton
                title={file.fileName}
                icon={
                  <FileIconOne
                    color={
                      selectedFile === file
                        ? theme.colors.primary
                        : theme.colors.textSecondary
                    }
                  />
                }
                color={
                  selectedFile === file
                    ? theme.colors.primary
                    : theme.colors.textSecondary
                }
                onClick={() => onClickFile(file)}
              />
              <HorizontalSpacer spacing={2} />
            </div>
          ))}
        </Col>
        <Col lg={9}>
          {error && (
            <Alert
              type="error"
              title={t("documentHandler.thereWasAnErrorFetchingTheDocument")}
              message={error?.toString()}
            />
          )}
          {loading && <LoadingSpinner />}
          {pdfUrl && (
            <>
              <Row justify="between" align="center" nogutter>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {numPages && numPages > 1 && (
                    <OutlinedButton
                      icon={
                        <ChevronLeftIconLarge
                          color={
                            currentPage <= 1
                              ? theme.colors.white
                              : theme.colors.text
                          }
                        />
                      }
                      title={t("documentHandler.previousPage")}
                      disabled={currentPage <= 1}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      color={theme.colors.text}
                    />
                  )}
                  <Body>
                    {t("common.page")} {currentPage} av {numPages}
                  </Body>
                  {numPages && numPages > 1 && (
                    <OutlinedButton
                      iconRight={
                        <ChevronRightIconLarge
                          color={
                            currentPage >= (numPages ?? 1)
                              ? theme.colors.white
                              : theme.colors.text
                          }
                        />
                      }
                      disabled={numPages ? currentPage >= numPages : true}
                      title={t("documentHandler.nextPage")}
                      color={theme.colors.text}
                      onClick={() =>
                        setCurrentPage((prev) =>
                          numPages ? Math.min(prev + 1, numPages) : prev + 1
                        )
                      }
                    />
                  )}
                </div>
                <OutlinedButton
                  medium
                  title={t("common.download")}
                  icon={<DownloadIcon color={theme.colors.primary} />}
                  onClick={handleDownload}
                  color={theme.colors.primary}
                />
              </Row>
              <HorizontalSpacer spacing={1} />

              <Document
                key={selectedFile.fileId}
                file={memoizedFile}
                onLoadError={(err) => console.error("PDF load error:", err)}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                <Page
                  pageNumber={currentPage}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PreviewDocumentsModalContent;
